import { useState, useEffect } from "react";
import axios from "../../api/axios";
import SidebarAdmin from "../../components/SidebarAdmin";

export default function ServicePlans() {
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [newPlan, setNewPlan] = useState({ name: "", price: "", duration_days: 30 });

  const fetchPlans = async () => {
    try {
      const res = await axios.get("/admin/plans");
      setPlans(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleAddPlan = async (e) => {
    e.preventDefault();
    await axios.post("/admin/plans", { 
      name: newPlan.name, 
      price: newPlan.price,
      duration_days: Number(newPlan.duration_days || 30),
    });
    setShowModal(false);
    setNewPlan({ name: "", price: "", duration_days: 30 });
    fetchPlans();
  };

  const handleUpdatePlan = async (e) => {
    e.preventDefault();
    if (!editPlan?.id) return;

    try {
      await axios.put(`/admin/plans/${editPlan.id}`, {
        name: editPlan.name,
        price: editPlan.price,
        duration_days: Number(editPlan.duration_days || 30),
      });
      setEditPlan(null);
      fetchPlans();
    } catch (err) {
      console.error("Update plan failed:", err);
      alert(err?.response?.data?.error || "Failed to update plan");
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!planId) return;
    const ok = window.confirm("Delete this plan? This cannot be undone.");
    if (!ok) return;

    try {
      await axios.delete(`/admin/plans/${planId}`);
      setEditPlan(null);
      fetchPlans();
    } catch (err) {
      console.error("Delete plan failed:", err);
      alert(err?.response?.data?.error || "Failed to delete plan");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarAdmin />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-800">Subscription Plans</h2>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 md:px-6 py-2 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition"
          >
            + Create New Plan
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-3xl shadow-sm border-2 border-transparent hover:border-blue-500 transition-all group relative"
            >
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">{plan.name}</h3>
              <p className="text-3xl sm:text-4xl font-black text-gray-900 my-4">
                ₹{plan.price}
                <span className="text-sm text-gray-400 font-normal">/{plan.duration_days || 30} days</span>
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center font-medium text-gray-700">
                  <span className="mr-2 text-green-500 font-bold">✓</span>
                  {plan.message_limit == null || parseInt(plan.message_limit) === -1 ? (
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">Unlimited Messages</span>
                  ) : (
                    `${plan.message_limit} Messages /mo`
                  )}
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="mr-2 text-green-500">✓</span> WhatsApp API Access
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="mr-2 text-green-500">✓</span> Priority Support
                </li>
              </ul>
              
              <button
                onClick={() => setEditPlan({ ...plan })}
                className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition"
              >
                Manage Plan
              </button>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 sm:p-8 rounded-3xl w-full max-w-md shadow-2xl">
              <h3 className="text-2xl font-bold mb-6 text-gray-800">Create New Plan</h3>
              <form onSubmit={handleAddPlan} className="space-y-4">
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Plan Name (e.g. Unlimited Pro)"
                  required
                  value={newPlan.name}
                  onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                />
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  type="number"
                  placeholder="Price (INR)"
                  required
                  value={newPlan.price}
                  onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                />

                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  type="number"
                  placeholder="Duration (days)"
                  min={1}
                  required
                  value={newPlan.duration_days}
                  onChange={(e) => setNewPlan({ ...newPlan, duration_days: e.target.value })}
                />

                <div className="text-xs text-gray-500 font-bold">
                  MESSAGE LIMIT: <span className="text-green-700">Unlimited</span>
                </div>

                <div className="flex gap-3 mt-6">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-600">Cancel</button>
                  <button type="submit" className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg">Save Plan</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editPlan && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 sm:p-8 rounded-3xl w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Manage Plan</h3>
                <button onClick={() => setEditPlan(null)} className="text-gray-400 font-bold">✕</button>
              </div>

              <form onSubmit={handleUpdatePlan} className="space-y-4">
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Plan Name"
                  required
                  value={editPlan.name}
                  onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })}
                />
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  type="number"
                  placeholder="Price (INR)"
                  required
                  value={editPlan.price}
                  onChange={(e) => setEditPlan({ ...editPlan, price: e.target.value })}
                />

                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  type="number"
                  placeholder="Duration (days)"
                  min={1}
                  required
                  value={editPlan.duration_days || 30}
                  onChange={(e) => setEditPlan({ ...editPlan, duration_days: e.target.value })}
                />

                <div className="text-xs text-gray-500 font-bold">
                  MESSAGE LIMIT: <span className="text-green-700">Unlimited</span>
                </div>

                <div className="flex gap-3 mt-6">
                  <button type="button" onClick={() => setEditPlan(null)} className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-600">Cancel</button>
                  <button type="submit" className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg">Save</button>
                </div>

                <button
                  type="button"
                  onClick={() => handleDeletePlan(editPlan.id)}
                  className="w-full mt-3 bg-red-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-red-700 transition"
                >
                  Delete Plan
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}